import { useMutation, useQuery, useQueryClient, QueryKey } from '@tanstack/react-query';

interface CrudResourceConfig<T, CreateDto, UpdateDto> {
  queryKey: QueryKey;
  fetchList: () => Promise<T[]>;
  create?: (dto: CreateDto) => Promise<T | void>;
  update?: (id: number, dto: UpdateDto) => Promise<T | void>;
  remove?: (id: number) => Promise<void>;
  invalidateKeys?: QueryKey[];
  retry?: number;
}

/**
 * Wiring de react-query repetido en cada hook de recurso CRUD: query de lista +
 * mutations create/update/delete + invalidación de caché. No cubre queries
 * secundarias ni mutations específicas de una entidad — esas se quedan en el
 * hook que envuelve a este factory.
 */
export function useCrudResource<T, CreateDto = Partial<T>, UpdateDto = Partial<T>>(
  config: CrudResourceConfig<T, CreateDto, UpdateDto>
) {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: config.queryKey });
    config.invalidateKeys?.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
  };

  const {
    data: items,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: config.queryKey,
    queryFn: config.fetchList,
    retry: config.retry ?? 2,
    retryDelay: 1000,
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateDto) => {
      if (!config.create) throw new Error('create no está configurado para este recurso');
      return config.create(dto);
    },
    onSuccess: invalidateAll,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDto }) => {
      if (!config.update) throw new Error('update no está configurado para este recurso');
      return config.update(id, data);
    },
    onSuccess: invalidateAll,
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => {
      if (!config.remove) throw new Error('remove no está configurado para este recurso');
      return config.remove(id);
    },
    onSuccess: invalidateAll,
  });

  return {
    items: items ?? [],
    isLoading,
    isFetching,
    error,
    refetch,
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    remove: removeMutation.mutate,
    removeAsync: removeMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRemoving: removeMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    removeError: removeMutation.error,
  };
}
